<template>
  <div class="workflow-controls-wrapper">
    <!-- Strategic engine button -->
    <v-tooltip v-if="!isUnitManagerView" bottom content-class="engine-tooltip">
      <template #activator="{ on, attrs }">
        <!-- span wrapper required so hover fires even when button is disabled -->
        <span v-bind="attrs" v-on="on">
          <v-btn
            depressed
            :loading="calculatingScenarioResults"
            :disabled="!isEditable || !engineCanRun || calculatingScenarioElasticityResults"
            :class="[
              'refresh-button',
              'mr-2',
              `${hasUnsavedKpiResults && !calculatingScenarioResults ? 'unsaved-kpis' : ''}`,
            ]"
            data-dd-action-name="Grid view recalculate all"
            @click="recalculate()"
          >
            <cog-play-icon />
          </v-btn>
        </span>
      </template>
      <template
        v-if="engineCanRun && !calculatingScenarioElasticityResults && !hasUnsavedKpiResults"
      >
        <div>{{ $t('gridView.tooltips.strategicOptimiser') }}</div>
        <v-divider class="my-2" dark />
        <last-triggered-info
          :last-triggered-info="get(lastTriggeredInfo.strategic_engine_full_run, scenario, {})"
          :loading="isLastTriggeredLoading.strategic_engine"
          source="strategic_engine_full_run"
          :width="200"
        />
      </template>
      <template v-else>
        <div>{{ $t('gridView.tooltips.strategicOptimiser') }}</div>
        <v-divider class="my-2" dark />
        <div v-if="isString(engineButtonTooltipMsg)">
          {{ engineButtonTooltipMsg }}
        </div>
        <div v-else>
          <div
            v-for="(val, key) in engineButtonTooltipMsg"
            :key="key"
            class="engine-blocked-tooltip-item"
          >
            <span>{{ key }}:</span> <span>{{ val }}</span>
          </div>
        </div>
        <v-divider class="my-2" dark />
        <last-triggered-info
          :last-triggered-info="get(lastTriggeredInfo.strategic_engine_full_run, scenario, {})"
          :loading="isLastTriggeredLoading.strategic_engine"
          source="strategic_engine_full_run"
          :width="200"
        />
      </template>
    </v-tooltip>

    <!-- Cross-elasticity engine button -->
    <feature-toggle v-if="!isUnitManagerView" :toggle="enablePriceOptimisation">
      <feature-toggle :toggle="displayPriceOptimisationButtonOnlyForPriceList" :inverted="true">
        <v-tooltip bottom content-class="engine-tooltip">
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                depressed
                :loading="calculatingScenarioElasticityResults"
                :disabled="!canRunOptimisation"
                :class="['refresh-button', 'cross-elasticity-button', 'mr-2']"
                data-dd-action-name="Grid view cross-elasticity run"
                @click="runCrossElasticityOptimisation()"
              >
                <circles-play-icon />
              </v-btn>
            </span>
          </template>
          <template v-if="canRunOptimisation">
            <div>{{ $t('gridView.tooltips.crossElasticityOptimiser') }}</div>
            <v-divider class="my-2" dark />
            <last-triggered-info
              :last-triggered-info="get(lastTriggeredInfo.cross_elasticity_engine, scenario, {})"
              :loading="isLastTriggeredLoading.cross_elasticity_engine"
              source="cross_elasticity_engine"
              translation-time-key="gridView.optimisationLastRunTime"
              :width="200"
            />
          </template>
          <template v-else>
            <div>{{ $t('gridView.tooltips.crossElasticityOptimiser') }}</div>
            <v-divider class="my-2" dark />
            <div>{{ crossElasticityButtonTooltipMsg }}</div>
            <v-divider class="my-2" dark />
            <last-triggered-info
              :last-triggered-info="get(lastTriggeredInfo.cross_elasticity_engine, scenario, {})"
              :loading="isLastTriggeredLoading.cross_elasticity_engine"
              source="cross_elasticity_engine"
              translation-time-key="gridView.optimisationLastRunTime"
              :width="200"
            />
          </template>
        </v-tooltip>
      </feature-toggle>
    </feature-toggle>

    <!-- Store group engines runs badges -->
    <div v-if="showToolStoreGroup && sortedStoreGroups.length" class="store-group-badges">
      <v-tooltip
        v-for="sg in sortedStoreGroups"
        :key="sg.toolStoreGroupKey"
        bottom
        :disabled="!storeGroupBadgeClass(sg.toolStoreGroupKey)"
      >
        <template #activator="{ on, attrs }">
          <span
            :class="['store-group-badge', storeGroupBadgeClass(sg.toolStoreGroupKey)]"
            v-bind="attrs"
            v-on="on"
          >
            {{ sg.toolStoreGroupDescription }}
          </span>
        </template>
        {{ storeGroupBadgeTooltip(sg.toolStoreGroupKey) }}
      </v-tooltip>
    </div>

    <v-divider v-if="!isUnitManagerView" vertical class="mx-3" />

    <!-- Bulk actions button-->
    <feature-toggle v-if="!isUnitManagerView" :toggle="allowBulkPriceOverrides">
      <v-tooltip bottom :disabled="!isBulkActionsDisabled">
        <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <v-menu offset-y>
            <template #activator="{ on: menuOn, attrs: menuAttrs }">
              <!-- span wrapper required so hover fires even when button is disabled -->
              <span v-bind="tooltipAttrs" v-on="tooltipOn">
                <v-btn
                  outlined
                  small
                  :disabled="isBulkActionsDisabled"
                  :loading="bulkSelectionState.isLoading"
                  class="bulk-actions-button pa-0 mr-2"
                  v-bind="menuAttrs"
                  v-on="menuOn"
                >
                  <v-icon small class="mr-1">mdi-menu-down</v-icon>
                  {{ $t('gridView.bulkActions.label') }}
                  <span v-if="bulkSelectionState.selectedCount > 0" class="ml-1">
                    ({{ bulkSelectionState.selectedCount }})
                  </span>
                </v-btn>
              </span>
            </template>
            <v-list dense>
              <v-list-item
                v-for="item in bulkPriceOverrideMenuItems"
                :key="item.action"
                @click="onBulkPriceOverrideSelected(item.action)"
              >
                <v-list-item-title>{{ $t(item.labelKey) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        {{ bulkActionsTooltip }}
      </v-tooltip>
    </feature-toggle>

    <!-- Save changes button -->
    <v-tooltip v-if="readFromDatabricks" bottom :disabled="!hasUnsavedKpiResults">
      <template #activator="{ on, attrs }">
        <!-- span wrapper required so hover fires even when button is disabled -->
        <span v-bind="attrs" v-on="on">
          <v-btn
            outlined
            small
            depressed
            :disabled="!hasUnsavedKpiResults"
            :loading="savingKpiResults"
            :class="['save-button', 'pa-0', `${hasUnsavedKpiResults ? 'unsaved-kpis' : ''}`]"
            @click="onSaveKpiResultsToDatabricks"
          >
            {{ $t('gridView.saveChanges') }}
          </v-btn>
        </span>
      </template>
      {{ $t('gridView.tooltips.hasUnsavedKpiResults') }}
    </v-tooltip>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { get, isEmpty, isString, uniq } from 'lodash';

import { allowBulkPriceOverrides, showAdditionalCostTypes } from '@enums/feature-flags';
import bulkPriceOverrideActions from '@enums/bulk-price-override-actions';
import featureFlagsMixin from '../../../mixins/featureFlags';
import engineControlsMixin from '../../../mixins/engineControls';

const bulkPriceOverrideConfig = [
  {
    action: bulkPriceOverrideActions.overrideWithEnginePrice,
    event: 'bulk-override-with-engine-price',
    labelKey: 'gridView.bulkActions.overrideWithProposed',
  },
  {
    action: bulkPriceOverrideActions.overrideWithIntentionPrice,
    event: 'bulk-override-with-intention-price',
    labelKey: 'gridView.bulkActions.overrideWithIntention',
  },
  {
    action: bulkPriceOverrideActions.overrideWithCostPriceDelta,
    event: 'bulk-override-with-cost-price-delta',
    labelKey: 'gridView.bulkActions.overrideWithCostPriceDelta',
  },
  {
    action: bulkPriceOverrideActions.overrideWithTransferPriceDelta,
    event: 'bulk-override-with-transfer-price-delta',
    labelKey: 'gridView.bulkActions.overrideWithTransferPriceDelta',
    featureFlag: showAdditionalCostTypes,
  },
  {
    action: bulkPriceOverrideActions.overrideWithPurchasePriceDelta,
    event: 'bulk-override-with-purchase-price-delta',
    labelKey: 'gridView.bulkActions.overrideWithPurchasePriceDelta',
    featureFlag: showAdditionalCostTypes,
  },
  {
    action: bulkPriceOverrideActions.removeAllOverrides,
    event: 'bulk-remove-all-overrides',
    labelKey: 'gridView.bulkActions.removeAllOverrides',
  },
];

export default {
  name: 'EngineControls',

  mixins: [featureFlagsMixin, engineControlsMixin],

  props: {
    levelEntryKey: {
      type: String,
      required: true,
    },
    pricingGroupName: {
      type: String,
      default: '',
    },
    showToolStoreGroup: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    isUnitManagerView: {
      type: Boolean,
    },
  },

  data() {
    return {
      engineCanRun: false,
      engineBlockedMsg: '',
      crossElasticityBlockedMsg: '',
      allowBulkPriceOverrides,
      isString,
    };
  },

  computed: {
    ...mapState('gridView', ['savingKpiResults', 'loadingItems', 'bulkSelectionState']),
    ...mapGetters('gridView', ['readFromDatabricks']),
    ...mapGetters('storeGroupRelationships', ['sortedStoreGroups']),

    scenariosWithUnsavedKpiResults() {
      return uniq(
        this.items.filter(item => item.hasUnsavedKpiResults).map(item => item.scenarioKey)
      );
    },

    engineButtonTooltipMsg() {
      if (this.hasUnsavedKpiResults) {
        return this.$t('gridView.tooltips.hasUnsavedKpiResults');
      }
      if (this.calculatingScenarioElasticityResults) {
        return this.$t('gridView.tooltips.crossElasticityOptimisationRunning');
      }
      return this.engineBlockedMsg;
    },

    crossElasticityButtonTooltipMsg() {
      if (this.isWeeklyWorkpackage) {
        return this.$t('gridView.tooltips.notAllowedInWeeklyWorkpackage');
      }
      if (this.hasUnsavedKpiResults) {
        return this.$t('gridView.tooltips.hasUnsavedKpiResults');
      }
      if (this.calculatingScenarioResults) {
        return this.$t('gridView.tooltips.engineRunning');
      }
      if (!this.hasAOExtracts) {
        return this.$t('gridView.tooltips.missingAOExtracts');
      }
      return this.crossElasticityBlockedMsg;
    },

    canRunOptimisation() {
      return (
        this.isEditable &&
        !this.isWeeklyWorkpackage &&
        !this.hasUnsavedKpiResults &&
        !this.calculatingScenarioResults &&
        this.hasAOExtracts
      );
    },

    bulkPriceOverrideMenuItems() {
      return this.filterArrayByFeatureFlag(bulkPriceOverrideConfig);
    },

    isBulkActionsDisabled() {
      return (
        this.bulkSelectionState.selectedCount === 0 ||
        this.loadingItems ||
        this.bulkSelectionState.hasLock ||
        this.bulkSelectionState.multiTsg ||
        this.bulkSelectionState.isLoading
      );
    },

    bulkActionsTooltip() {
      if (this.bulkSelectionState.selectedCount === 0) {
        return this.$t('gridView.bulkActions.noSelectionTooltip');
      }
      if (this.loadingItems) {
        return this.$t('gridView.bulkActions.loadingTooltip');
      }
      if (this.bulkSelectionState.hasLock) {
        return this.$t('gridView.bulkActions.lockedTooltip');
      }
      if (this.bulkSelectionState.multiTsg) {
        return this.$t('gridView.bulkActions.multiTsgTooltip');
      }
      if (this.bulkSelectionState.isLoading) {
        return this.$t('gridView.bulkActions.operationInProgressTooltip');
      }
      return '';
    },
  },

  async created() {
    // Check if engine can run
    const { canEngineRun, missingProduct, invalidProduct } = await this.canEngineRun({
      pricingGroupId: this.levelEntryKey,
    });
    this.engineCanRun = canEngineRun;

    if (!this.engineCanRun) {
      // Format message to inform user
      const getProductError = p =>
        this.showToolStoreGroup
          ? `${p.productKeyDisplay} ${this.$t('pricing.inStoregroup')}: ${
              p.toolStoreGroupDescription
            }`
          : p.productKeyDisplay;

      this.engineBlockedMsg = {
        [this.$t('pricing.error')]: this.$t('pricing.engineCannotRun', {
          pricingGroupName: this.pricingGroupName || '',
        }),
      };

      if (missingProduct) {
        this.engineBlockedMsg[this.$t('pricing.missing')] = getProductError(missingProduct);
      }
      if (invalidProduct) {
        this.engineBlockedMsg[this.$t('pricing.invalid')] = getProductError(invalidProduct);
      }
    }
  },

  methods: {
    ...mapActions('gridView', ['canEngineRun', 'saveKpiResultsToDatabricks']),
    async recalculate() {
      this.handleRecalculate({
        scenarioKey: this.scenario,
        workpackageId: this.selectedWorkpackage._id,
      });
    },

    async onSaveKpiResultsToDatabricks() {
      await this.saveKpiResultsToDatabricks({
        scenarioKeys: this.scenariosWithUnsavedKpiResults,
        levelEntryKey: this.levelEntryKey,
        isUnitManagerView: this.isUnitManagerView,
      });
    },

    async runCrossElasticityOptimisation() {
      this.handleCrossElasticityOptimisation({
        scenarioKey: this.scenario,
        workpackageId: this.selectedWorkpackage._id,
      });
    },

    onBulkPriceOverrideSelected(action) {
      this.$emit('bulk-action-selected', action);
    },

    /**
     * Returns a CSS modifier class for a store group badge based on the last triggered engine run.
     * Uses lastTriggeredInfo.strategic_engine_full_run for the strategic engine (excludes skipOptimisation runs).
     * When both engines have a qualifying run, the most recently triggered one wins.
     *
     * @param {string} toolStoreGroupKey - The store group key to check.
     * @returns {string} CSS class name, or empty string if no qualifying run info is available.
     */
    storeGroupBadgeClass(toolStoreGroupKey) {
      const seoRecord = get(this.lastTriggeredInfo, [
        'strategic_engine_full_run',
        toolStoreGroupKey,
      ]);
      const xeoRecord = get(this.lastTriggeredInfo, ['cross_elasticity_engine', toolStoreGroupKey]);
      const hasSeo = !isEmpty(seoRecord);
      const hasXeo = !isEmpty(xeoRecord);

      if (hasSeo && hasXeo) {
        return new Date(xeoRecord.date) >= new Date(seoRecord.date)
          ? 'store-group-badge--xeo'
          : 'store-group-badge--seo';
      }
      if (hasXeo) return 'store-group-badge--xeo';
      if (hasSeo) return 'store-group-badge--seo';
      return '';
    },

    /**
     * Returns a tooltip translation string for a store group badge.
     *
     * @param {string} toolStoreGroupKey - The store group key to check.
     * @returns {string} Translated tooltip text, or empty string when no run info is available.
     */
    storeGroupBadgeTooltip(toolStoreGroupKey) {
      const cls = this.storeGroupBadgeClass(toolStoreGroupKey);
      if (cls === 'store-group-badge--seo') return this.$t('gridView.tooltips.storeGroupBadgeSeo');
      if (cls === 'store-group-badge--xeo') return this.$t('gridView.tooltips.storeGroupBadgeXeo');
      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.refresh-button {
  background-color: $pricing-primary !important;
  color: white;
  min-width: unset !important;
  padding: unset !important;
  height: 3rem !important;
  width: 3rem;

  &.unsaved-kpis {
    background-color: $pricing-orange !important;
  }
}

.cross-elasticity-button {
  background-color: $pricing-blue !important;
}

.save-button {
  &.v-btn--disabled {
    border-color: $pricing-grey !important;
    color: $pricing-grey-dark !important;
  }

  &.unsaved-kpis {
    background-color: $pricing-orange;
    border: none;
    color: white !important;
  }
}

.workflow-controls-wrapper {
  display: flex;
  align-items: center;
}

.engine-blocked-tooltip-item {
  display: flex;
  gap: 4px;
}

.bulk-actions-button.v-btn--outlined {
  border-color: $pricing-primary;
  color: $pricing-primary;

  &.v-btn--disabled {
    border-color: $pricing-grey !important;
    color: $pricing-grey-dark !important;
  }
}

.store-group-badges {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
}

.store-group-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  background-color: $pricing-grey-border;
  color: $pricing-grey-3;

  &--seo {
    background-color: $pricing-green-light;
    color: $pricing-green-dark;
  }

  &--xeo {
    background-color: $pricing-green-dark;
    color: $pricing-green-subtle;
  }
}
</style>

<style lang="scss">
@import '@style/base/_variables.scss';

.engine-tooltip {
  .last-triggered-info {
    padding: 4px 0 !important;
  }
  // last-triggered-info uses scoped dark text; override to white inside the dark engine tooltip
  .last-run-text {
    color: $pricing-white !important;
    font-size: 12px !important;
  }
}
</style>
